import OLDraw, { DrawEvent } from 'ol/interaction/Draw';
import { unByKey } from 'ol/Observable';
import Feature from 'ol/Feature';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import Point from 'ol/geom/Point';
import Event from 'ol/events/Event';
import { Polygon } from 'ol/geom';

import { SketchTextInput } from 'cadenza/sketch-text-input/sketch-text-input';
import { Rectangle } from 'cadenza/map/geom/rectangle';
import { rectangleGeometryFunction } from 'cadenza/map/geom/rectangle-utilities';
import { ovalGeometryFunction } from 'cadenza/map/geom/oval-utilties';
import { lineGeometryFunction } from 'cadenza/map/geom/line';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import {
  isOLDrawSnapToFinish, isOvalDrawCoordValidToDraw,
  isPolygonDrawCoordValidToDraw,
  isPolygonWithDrawCoordValidToFinish,
  isPolygonWithoutDrawCoordValidToFinish, isRectangleDrawCoordValidToDraw
} from 'cadenza/map/interactions/draw-interaction-validation';
import { cloneStyle } from 'cadenza/map/style/style-util';
import { spawnPolygonMergeStrategyWorker } from 'cadenza/map/polygon-merge-strategy/polygon-merge-strategy';
import { createSnapStyle, updateOverlayStyle } from 'cadenza/map/interactions/snap-utils';
import { TooltipOverlay } from 'cadenza/map/tooltip/tooltip-overlay';
import { FeatureGeometryEditorEvent } from 'cadenza/map/feature-geometry-editor/feature-geometry-editor-event-bus';
import { toOLSingleGeometryType } from 'cadenza/utils/ol-geometry-util';
import { timeout } from 'cadenza/utils/timeout';
import { Oval } from 'cadenza/map/geom/oval';

import i18n from './interactions.properties';

export const FEATURE_IN_PROGRESS_POTENTIALLY_DONE = 'featureInProgressPotentiallyDone';

export const DRAW_INTERACTION_EVENT = {
  DRAW_ABORT: 'drawabort',
  DRAW_END: 'drawend',
  DRAW_START: 'drawstart',
  INVALID_VERTEX: 'draw:invalid-vertex',
  INVALID_GEOMETRY: 'draw:invalid-geometry'
};

export class DrawInteractionEvent extends Event {}

// Draw interaction extends OpenLayers Draw interaction with the following features:
// - help tooltip,
// - aborts drawing on Esc,
// - 'drawabort' event,
// - 'Oval', 'Rectangle' and 'Line' geometry types.
export class DrawInteraction extends OLDraw {

  /**
   * Creates a DrawInteraction instance
   *
   * @param {import("ol").Map} map - The OL map
   * @param {object} options - the options
   * @param {EditableGeometryType | EditablePolygonVariantType} [options.type] - What kind of geometry are we drawing
   * @param {VectorLayerSource} [options.source] - The source to which we will add the drawn feature
   * @param {StyleLike} [options.style] - The style for features while they are being drawn
   * @param {StyleLike} [options.textStyle] - This style is used to style text input fields when drawing features with text
   * @param {PolygonMergeStrategyType} [options.polygonMergeStrategy] - The strategy used for merging the drawn polygon into the layer
   * @param {FeatureGeometryEditorEventBus} [options.eventBus] - Used for propagating events
   * @param {boolean} [options.isTextModeDrawing] - Whether we're creating a text sketch item
   * @param {boolean} [options.clearOnDrawEnd] - Whether the underlying source is cleared after each draw, meaning only the latest drawn geometry is kept (This flag has no effect when working with multi-geometry type). Default is false.
   * @param {boolean} [options.isWorkbook] - This flag is used to distinguish whether the usage context comes from workbooks. Default is false.
   */
  constructor (map, options) {
    const type = options.type;
    options = sanitizeOptions({
      ...options,
      shiftPressedCondition: () => this._shiftPressed,
      condition: () => this._checkDrawingAllowed(),
      freehandCondition: () => false
    });

    super(options);
    this.setActive(false);

    this._map = map;
    this._tooltip = new TooltipOverlay(map, {
      showArrow: false,
      positioning: 'center-right'
    });
    this._type = type;
    this._isTextModeDrawing = options.isTextModeDrawing;
    this._textStyle = options.textStyle;
    this._isWorkbook = options.isWorkbook;
    this._style = options.style;
    this._snapStyle = createSnapStyle();
    this._currentDrawFeature = null;
    this._textFeatureCoord = null;
    this._source = options.source;
    this._polygonMergeStrategy = options.polygonMergeStrategy;
    this._clearOnDrawEnd = options.clearOnDrawEnd ?? false;
    this._eventBus = options.eventBus;
    this._validateDrawCoord = getDrawValidationForType(type);
    if (this._isTextModeDrawing) {
      this._sketchTextInput = this._createSketchTextInput(options.textStyle);
    }

    this._baseHandlers = {
      drawstart: event => {
        if (event.dragging) {
          return;
        }
        this._currentDrawFeature = event.feature;
        this._updateTooltipMessage();
      },
      drawend: event => {
        this._currentDrawFeature = null;
        sanitizeFeature(event.feature);
        this._updateTooltipMessage();
        this._onDrawEnd(event.feature);
      },
      pointermove: event => {
        this._shiftPressed = event.originalEvent.shiftKey;
        if (event.dragging) {
          return;
        }

        this._updateTooltipMessage();
      },
      pointerdblclick: () => {
        // the geometry was just finished before
        if (!this._currentDrawFeature) {
          return;
        }
        // if we have a draw feature which is not a polygon
        // this means the feature failed the validation on the first click,
        // and thus we will not allow to finish here either.
        if (getDrawMode(this._type) !== 'Polygon') {
          return;
        }

        const geometry = this._currentDrawFeature.getGeometry();
        if (!isPolygonWithoutDrawCoordValidToFinish(geometry)) {
          // we don't dispatch an invalid-polygon event here, since it would
          // be duplicated from the pointer-up event.
          return;
        }
        this.finishDrawing();
      },
      keydown: event => { this._handleKeyDown(event); }
    };

    this.on('change:active', () => {
      if (this.getActive()) {
        this._onActivate();
      } else {
        this._onDeactivate();
      }
    });
  }

  _subscribeToPolygonMergeStrategyChange (eventBus) {
    return eventBus.on(FeatureGeometryEditorEvent.CHANGE_POLYGON_MERGE_STRATEGY, event => {
      this._polygonMergeStrategy = event.detail.polygonMergeStrategy;
    });
  }

  _checkDrawingAllowed () {
    if (!this._currentDrawFeature) {
      return true;
    }
    const geometry = this._currentDrawFeature.getGeometry();
    const isDrawCoordValid = this._validateDrawCoord(geometry);

    if (!(getDrawMode(this._type) === 'Polygon')) {
      if (!isDrawCoordValid) {
        this.dispatchEvent(new DrawInteractionEvent(DRAW_INTERACTION_EVENT.INVALID_GEOMETRY));
        return false;
      }
      return true;
    }

    if (isOLDrawSnapToFinish(geometry)) {
      if (isPolygonWithoutDrawCoordValidToFinish(geometry)) {
        // let draw.js handle finishing
        return true;
      }
      this.dispatchEvent(new DrawInteractionEvent(DRAW_INTERACTION_EVENT.INVALID_GEOMETRY));
      return false;
    }

    if (!isDrawCoordValid) {
      this.dispatchEvent(new DrawInteractionEvent(DRAW_INTERACTION_EVENT.INVALID_VERTEX));
    }

    // if we allow to add a new coord,
    // so we can offer to finish the drawing
    if (isDrawCoordValid && hasRequiredMinCoords(geometry)) {
      this._tooltip.message = i18n('draw-base.finishDrawing');
    }
    return isDrawCoordValid;
  }

  handleEvent (event) {
    if (!this._isTextModeDrawing) {
      updateOverlayStyle(event.snapped, this, this._style, this._snapStyle);
      return super.handleEvent(event);
    }
    return this._handleTextDrawEvent(event);
  }

  _handleTextDrawEvent (event) {
    if (event.type === MapBrowserEventType.POINTERDOWN) {
      this._textModeAndDragging = false;
      // remove control from draw.js since drawing text is handled by this class
      // we still want to propagate the event (i.e. pan map,..)
      return true;
    }

    // if we drag the map, we don't want to add text input on pointerup
    if (event.type === MapBrowserEventType.POINTERDRAG) {
      this._textModeAndDragging = true;
    }

    if (!this._textModeAndDragging && event.type === MapBrowserEventType.POINTERUP) {
      this._startTextInput(event.coordinate, event.pixel);
      // we still want to propagate the event (i.e. pan map,..)
      return true;
    }
    return super.handleEvent(event);
  }

  _updateTooltipMessage () {
    this._tooltip.message = this._getTooltipMessage();
  }

  _getTooltipMessage () {
    if (this._textFeatureCoord) {
      return i18n('draw-base.finishText');
    }

    if (!this._currentDrawFeature) {
      return i18n('draw-base.startDrawing');
    }

    switch (this._type) {
      case 'Oval':
      case 'Rectangle':
      case 'Circle':
        return i18n('draw-base.finishDrawing');
      case 'Polygon':
      case 'MultiPolygon':
        return isOLDrawSnapToFinish(this._currentDrawFeature.getGeometry())
          ? i18n('draw-base.finishDrawing')
          : i18n('draw-base.continuePolygonDrawing');
      case 'Line':
        return i18n('draw-base.finishLineDrawing');
      case 'LineString':
      case 'MultiLineString':
        return i18n('draw-base.continueLineDrawing');
    }
  }

  _onActivate () {
    this._tooltip.activate();
    this._tooltip.show();

    this._eventKeys = [
      this.on('drawstart', this._baseHandlers.drawstart),
      this.on('drawend', this._baseHandlers.drawend),
      this._map.on('pointermove', this._baseHandlers.pointermove),
      this._map.on('dblclick', this._baseHandlers.pointerdblclick)
    ];
    if (this._eventBus) {
      this._eventKeys.push(this._subscribeToPolygonMergeStrategyChange(this._eventBus));
    }

    document.addEventListener('keydown', this._baseHandlers.keydown);
  }

  _onDeactivate () {
    this._tooltip.deactivate();

    unByKey(this._eventKeys);

    document.removeEventListener('keydown', this._baseHandlers.keydown);
  }

  _handleKeyDown (event) {
    if ((event.key === 'Enter')) {
      // no geometry is currently drawn
      // always the case in Point/Text mode
      if (!this._currentDrawFeature) {
        return;
      }
      const geometry = this._currentDrawFeature.getGeometry();

      if (!(getDrawMode(this._type) === 'Polygon' && hasRequiredMinCoords(geometry))) {
        if (this._validateDrawCoord(geometry)) {
          this._submitDraftCoord();
          this.finishDrawing();
          return;
        } else {
          this.dispatchEvent(new DrawInteractionEvent(DRAW_INTERACTION_EVENT.INVALID_GEOMETRY));
          return;
        }
      }

      // Since we have snap to finish for polygons, we have to check it first
      const polygonWithoutDrawCoordValidToFinish = isPolygonWithoutDrawCoordValidToFinish(geometry);
      if (isOLDrawSnapToFinish(geometry) && polygonWithoutDrawCoordValidToFinish) {
        this.finishDrawing();
        return;
      }

      if (isPolygonWithDrawCoordValidToFinish(geometry)) {
        this._submitDraftCoord();
        this.finishDrawing();
        return;
      }

      if (polygonWithoutDrawCoordValidToFinish) {
        this.finishDrawing();
        return;
      }

      this.dispatchEvent(new DrawInteractionEvent(DRAW_INTERACTION_EVENT.INVALID_GEOMETRY));
    }
    if ((event.key === 'Escape' || event.key === 'Esc' /* IE11 */)) {
      this._cancelDrawing();
      this.dispatchEvent(DRAW_INTERACTION_EVENT.DRAW_ABORT);
    }
  }

  _submitDraftCoord () {
    switch (this._type) {
      case 'Point':
      case 'Oval':
      case 'Rectangle':
        break;
      case 'Line':
      case 'LineString':
      case 'MultiLineString':
        this._submitLineStringDraftCoord();
        break;
      case 'Polygon':
      case 'MultiPolygon':
        this._submitPolygonDraftCoord();
        break;
      default:
        throw new Error(`Type not supported: ${this._type}`);
    }
  }

  _submitLineStringDraftCoord () {
    this.appendCoordinates(this._currentDrawFeature.getGeometry().getCoordinates().slice(-1));
  }

  _submitPolygonDraftCoord () {
    this.appendCoordinates(this._currentDrawFeature.getGeometry().getCoordinates()[0].slice(-2, -1));
  }

  _cancelDrawing () {
    if (this._textFeatureCoord) {
      this._textFeatureCoord = null;
      this._sketchTextInput?.hide();
    } else if (this._currentDrawFeature) {
      this._currentDrawFeature = null;
      this.abortDrawing();
    }
  }

  _startTextInput (coordinate, pixel) {
    this._textFeatureCoord = coordinate;
    this._sketchTextInput.show(pixel);
  }

  _createSketchTextInput (textStyle) {
    const sketchTextInput = new SketchTextInput(textStyle);
    sketchTextInput.addEventListener('cancel', () => this._cancelDrawing());
    sketchTextInput.addEventListener('finish', event => this._finishTextDraw(event.detail.text.trim()));
    this._map.getTargetElement().append(sketchTextInput);
    return sketchTextInput;
  }

  /**
   * Handler executed after the user finished entering sketch text item content
   *
   * @param text - text content from the input
   * @protected
   */
  _finishTextDraw (text) {
    const textFeature = new Feature({
      geometry: new Point(this._textFeatureCoord)
    });
    const style = cloneStyle(this._textStyle);
    style.getText().setText(text);
    textFeature.setStyle(style);
    if (!this._isWorkbook) {
      // In workbook context, features should not contain this property.
      // See modify-interaction.js#isClassicTextSketchFeature
      textFeature.set(SketchFeatureProperty.STYLE_ITEM, { lines: text.split('\n') });
    }

    this._source.addFeature(textFeature);

    this._textFeatureCoord = null;
    this._sketchTextInput.hide();

    this.dispatchEvent(new DrawEvent('drawend', textFeature));
  }

  async _onDrawEnd (feature) {
    if (isMergeable(this._type) && this._polygonMergeStrategy) {
      await this._mergePolygon(feature);
      return;
    }
    if (this._clearOnDrawEnd) {
      const source = this._source;
      source.clear();
    }
    // since we have an ol patch which can't be removed,
    // we have to wait for the next update until the feature is available (i.e. added to the source).
    // This workaround can and should be removed after the epic split
    // See:
    // Disy Patch: allow creation of geometry in an asynchronous way.
    // Issue: CADENZA-25206
    await timeout();
    this._eventBus?.dispatchEvent(FeatureGeometryEditorEvent.INTERACTION_FINISHED);
  }

  async _mergePolygon (feature) {
    const source = this._source;
    const polygonFeature = convertToPolygon(feature);
    const operators = [ polygonFeature.getGeometry() ];
    const base = source.getFeatures().map(baseFeature => baseFeature.getGeometry());
    const mergeResult = await spawnPolygonMergeStrategyWorker(this._polygonMergeStrategy)
      .merge(base, operators);
    source.clear();
    if (this._clearOnDrawEnd && mergeResult.length > 1) {
      this._eventBus?.trigger(FeatureGeometryEditorEvent.INTERACTION_FAILED, { message: i18n('draw-base.mergeFailed') });
      source.addFeature(polygonFeature);
    } else {
      source.addFeatures(mergeResult);
    }
    this._eventBus?.dispatchEvent(FeatureGeometryEditorEvent.INTERACTION_FINISHED);
  }

}

function convertToPolygon (feature) {
  const geometry = feature.getGeometry();
  if (geometry instanceof Oval) {
    const coordinates = geometry.generateCoordinates(0, 360, 5);
    return new Feature(new Polygon([ coordinates ]));
  }
  if (geometry instanceof Rectangle) {
    const coordinates = geometry.generateCoordinates();
    return new Feature(new Polygon([ coordinates ]));
  }
  return feature;
}

function sanitizeOptions (options) {
  // since oval/rectangle/Line are not natively supported by ol/draw we mimic it.
  if (options.type === 'Oval') {
    options.type = 'LineString';
    options.geometryFunction = (coords, geom) => ovalGeometryFunction(coords, geom, options.shiftPressedCondition());
    options.maxPoints = 2;
  } else if (options.type === 'Rectangle') {
    options.type = 'LineString';
    options.geometryFunction = (coords, geom) => rectangleGeometryFunction(coords, geom, options.shiftPressedCondition());
    options.maxPoints = 2;
  } else if (options.type === 'Line') {
    options.type = 'LineString';
    options.geometryFunction = lineGeometryFunction;
    options.maxPoints = 2;
  }
  options.type = toOLSingleGeometryType(options.type);
  return options;
}

function sanitizeFeature (feature) {
  const sketchGeometry = feature.getGeometry();
  if (sketchGeometry instanceof Rectangle) {
    sketchGeometry.normalize();
  }
}

function hasRequiredMinCoords (geometry) {
  switch (geometry.getType()) {
    case 'Point':
    case 'Oval':
    case 'Circle':
    case 'Rectangle':
      return true;
    case 'Line':
    case 'LineString':
      return geometry.getCoordinates().length > 1;
    case 'Polygon':
      return geometry.getCoordinates()[0].length > 3;
  }
}

/**
 * Get the drawing mode.  The mode for multi-part geometries is the same as for
 * their single-part cousins.
 * @param {import("../geom/Geometry.js").Type} type Geometry type.
 * @return {import("ol/interaction/Draw").Mode} Drawing mode.
 */
export function getDrawMode (type) {
  switch (type) {
    case 'Point':
    case 'MultiPoint':
      return 'Point';
    case 'Rectangle':
    case 'Oval':
    case 'Line':
    case 'LineString':
    case 'MultiLineString':
      return 'LineString';
    case 'Polygon':
    case 'MultiPolygon':
      return 'Polygon';
    case 'Circle':
      return 'Circle';
    default:
      throw new Error('Invalid type: ' + type);
  }
}

function isMergeable (type) {
  switch (type) {
    case 'Polygon':
    case 'MultiPolygon':
    case 'Rectangle':
    case 'Oval':
      return true;
    default:
      return false;
  }
}

function getDrawValidationForType (geometryType) {
  switch (geometryType) {
    case 'Polygon':
    case 'MultiPolygon':
      return (geometry) => isPolygonDrawCoordValidToDraw(geometry);
    case 'Rectangle':
      return (rectangle) => isRectangleDrawCoordValidToDraw(rectangle);
    case 'Oval':
      return (oval) => isOvalDrawCoordValidToDraw(oval);
    default:
      return () => true;
  }
}
